import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * 量「这一屏此刻实际有多宽」。
 *
 * # 为什么不用 window.innerWidth + resize 事件
 *
 * 第一版就是那么写的，然后在浏览器里实测发现：把视口从 1440 改到 1024 之后，
 * 会话栏还是 236px 的完整形态——**`resize` 事件没有让 React 重新算**。
 * 手动 `dispatchEvent(new Event('resize'))` 之后它立刻变成图标条，
 * 说明算法没错，错的是「什么时候重新算」这一步。
 *
 * 那次是被开发者工具改视口触发的，看起来像个测试环境的怪癖。**但它暴露的是
 * 一个真问题**：`window.innerWidth` 回答的是「窗口多宽」，而这一屏要的是
 * 「留给我多宽」。今天两者相等，只因为外壳没在左右放别的东西——
 * 哪天外壳加一条常驻侧栏，`innerWidth` 会多报出那一条的宽度，
 * 于是折叠判断会晚一步发生，而**表现是聊天列被挤窄，不是报错**。
 *
 * `ResizeObserver` 两个问题一起解决：它量的是元素而不是窗口，
 * 而且它不依赖任何事件送不送得到——布局变了它就响。
 *
 * # 观察的是外面那层，不是三条轨道那层
 *
 * 刻意让调用方把 ref 挂在**包住**三条轨道的那个容器上。挂在轨道自己身上会
 * 绕成一个环：宽度变 → 轨道重算 → 布局变 → 观察器再响。外层容器的宽度
 * 不受里面轨道怎么分影响，所以这个环从结构上就不存在，而不是靠「值没变
 * 所以 setState 不会重渲染」那种运气。
 */
export function useAvailableWidth(): [(node: HTMLElement | null) => void, number] {
  // 初值取窗口宽度：第一帧还没量到之前，用一个不会让布局跳一下的近似值。
  // 它只活到 ResizeObserver 第一次回调为止。
  const [width, setWidth] = useState(() =>
    typeof window === 'undefined' ? 1440 : window.innerWidth,
  )
  const observer = useRef<ResizeObserver | null>(null)

  useEffect(() => () => observer.current?.disconnect(), [])

  const ref = useCallback((node: HTMLElement | null) => {
    observer.current?.disconnect()
    observer.current = null
    if (!node) return

    // jsdom 没有 ResizeObserver。测试环境里退回一次性量一下——
    // **不静默什么都不做**：那会让宽度永远停在初值，而用例看不出区别。
    if (typeof ResizeObserver === 'undefined') {
      const measured = node.getBoundingClientRect().width
      if (measured > 0) setWidth(measured)
      return
    }

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      const measured = entry.contentRect.width
      // 0 宽度是「还没布局」或者「被藏起来了」，不是「窄到极限」。
      // 拿它去算折叠会把界面折成一条缝，而下一帧又弹回来。
      if (measured > 0) setWidth(measured)
    })
    ro.observe(node)
    observer.current = ro
  }, [])

  return [ref, width]
}
